import type { CataloguesResponse } from "@/features/boutique/types/catalogue.type.ts";
import type {
  EventRateGroup,
  EventsResponse,
  ReducceEvent,
} from "@/features/boutique/types/event.type.ts";
import type { OffersResponse } from "@/features/boutique/types/offer.type.ts";
import type {
  Article, Ref, RefsMapResponse, RefsResponse,
} from "@/features/boutique/types/ref.type.ts";
import type { RefDetailResponse } from "@/features/boutique/types/refDetail.type.ts";
import { fetcher } from "@/shared/lib/fetcher.client";

export const TICKET_CATALOGUE_ID = 5;

const getEventNumericId = (eventId: string): number => {
  const parsed = Number.parseInt(eventId, 10);
  if (!Number.isNaN(parsed)) {
    return parsed;
  }

  return eventId.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
};

const mapEventSessionsToArticles = (event: ReducceEvent): Article[] => {
  const basePrice = event.min_price_amount ?? "0";
  const numericId = getEventNumericId(event.id);

  if (!event.sessions?.length) {
    return [{
      id: numericId,
      name: event.name,
      kind: "event",
      original_price_amount: basePrice,
      discount_amount: "0",
      price_amount: basePrice,
      minimum_quantity: 1,
      multiple_quantity: 1,
      excerpt: null,
      description: null,
      details: null,
      quantities: [1],
    }];
  }

  return event.sessions.map((session, index) => ({
    id: (numericId * 1000) + index,
    name: (session.date || session.session_date)
      ? `${event.name} - ${session.date || session.session_date}`
      : event.name,
    kind: "event_session",
    original_price_amount: basePrice,
    discount_amount: "0",
    price_amount: basePrice,
    minimum_quantity: 1,
    multiple_quantity: 1,
    excerpt: session.code || session.session_code || null,
    description: null,
    details: null,
    quantities: [1],
  }));
};

const mapEventToRef = (event: ReducceEvent): Ref => {
  const numericId = getEventNumericId(event.id);
  const location = event.localization;

  return {
    id: numericId,
    event_id: event.id,
    name: event.name,
    image_url: event.image_url,
    discount_percentage: 0,
    articles: mapEventSessionsToArticles(event),
    excerpt: event.excerpt ?? undefined,
    description: event.description ?? undefined,
    dynamic_prices: event.dynamic_prices,
    localization: location
      ? {
        department_id: 0,
        department_name: location.city || location.name || "",
        region_name: location.region || "",
        latitude: location.latitude ?? null,
        longitude: location.longitude ?? null,
        name: location.name,
        address1: location.address1,
        address2: location.address2,
        postal_code: location.postal_code,
        city: location.city,
        region: location.region,
        country: location.country,
      }
      : null,
    sessions: event.sessions?.map((session) => ({
      session_code: session.session_code ?? session.code ?? "",
      session_date: session.session_date ?? session.date ?? "",
    })),
    category_name: event.category_name,
    subcategory_name: event.subcategory_name,
    min_price_amount: event.min_price_amount,
    status: event.status,
    maximum_quantity: event.maximum_quantity,
    conditions_tarifaires: event.conditions_tarifaires,
    placement: event.placement,
    plan: event.plan ?? null,
    type_billet: event.type_billet,
    type_billet_delivery_mode: event.type_billet_delivery_mode,
    has_seat_selection: event.has_seat_selection,
  };
};

const mapEventsResponseToRefs = (response: EventsResponse): RefsResponse => ({
  refs: response.events.map(mapEventToRef),
  metadata: response.metadata,
});

const mapEventDetailToRefDetail = (event: ReducceEvent): RefDetailResponse => {
  const ref = mapEventToRef(event);
  return { ref };
};

export const boutiqueService = {
  getCatalogues: async (): Promise<CataloguesResponse> => {
    const response = await fetcher.get("/rce-catalogues", { params: { with_categories: true } });
    return response.data;
  },

  getRefs: async (
    catalogueId: number,
    filters: {
      page?: number
      limit?: number
      search?: string
      categoryId?: number
      sort?: string
    },
  ): Promise<RefsResponse> => {
    const normalizedSearch = filters.search?.trim();
    const isTicketCatalogue = catalogueId === TICKET_CATALOGUE_ID;
    const params: Record<string, unknown> = {
      page: filters.page || 1,
      [isTicketCatalogue ? "per_page" : "limit"]: filters.limit || 12,
    };

    if (normalizedSearch) {
      params["q[name_cont]"] = normalizedSearch;
    }
    if (!normalizedSearch && filters.categoryId) {
      params["q[genre_id_eq]"] = filters.categoryId;
    }
    if (filters.sort) {
      if (isTicketCatalogue) {
        params["q[s]"] = filters.sort;
      }
      else {
        params.s = filters.sort;
      }
    }

    if (isTicketCatalogue) {
      const response = await fetcher.get<EventsResponse>(
        `/rce-catalogues/${catalogueId}/events`,
        { params },
      );
      return mapEventsResponseToRefs(response.data);
    }

    const response = await fetcher.get(`/rce-catalogues/${catalogueId}/refs`, { params });
    return response.data;
  },

  getRefsMap: async (
    catalogueId: number,
    filters: {
      search?: string
      categoryId?: number
      regionIds?: number[]
      departmentIds?: number[]
      priceRange?: string
    },
  ): Promise<RefsMapResponse> => {
    const parts: string[] = [];

    const normalizedSearch = filters.search?.trim();
    if (normalizedSearch) {
      parts.push(`q=${encodeURIComponent(normalizedSearch)}`);
    }

    if (filters.categoryId) {
      parts.push(`genre_id[]=${filters.categoryId}`);
    }

    if (filters.regionIds?.length) {
      for (const id of filters.regionIds) {
        parts.push(`region_id[]=${id}`);
      }
    }

    if (filters.departmentIds?.length) {
      for (const id of filters.departmentIds) {
        parts.push(`department_id[]=${id}`);
      }
    }

    if (filters.priceRange) {
      parts.push(`price_range=${encodeURIComponent(filters.priceRange)}`);
    }

    const queryString = parts.length > 0 ? `?${parts.join("&")}` : "";
    const response = await fetcher.get<RefsMapResponse>(
      `/rce-catalogues/${catalogueId}/search/map${queryString}`,
    );
    return response.data;
  },

  getRefById: async (
    catalogueId: number,
    refId: number,
  ): Promise<RefDetailResponse> => {
    const response = await fetcher.get(
      `/rce-catalogues/${catalogueId}/refs/${refId}`,
    );
    return response.data;
  },

  getEventById: async (
    catalogueId: number,
    eventId: string,
  ): Promise<RefDetailResponse> => {
    const response = await fetcher.get(
      `/rce-catalogues/${catalogueId}/events/${eventId}`,
    );
    return mapEventDetailToRefDetail(response.data.event);
  },

  getRefOffers: async (
    catalogueId: number,
    refId: number,
    params: {
      date: string
      days_count?: number | null
      experience?: string | null
    },
  ): Promise<OffersResponse> => {
    const queryParams: Record<string, unknown> = { date: params.date };

    if (params.days_count !== undefined && params.days_count !== null) {
      queryParams.days_count = params.days_count;
    }

    if (params.experience) {
      queryParams.experience = params.experience;
    }

    const response = await fetcher.get(
      `/rce-catalogues/${catalogueId}/refs/${refId}/offers`,
      { params: queryParams },
    );

    return response.data;
  },

  getEventRates: async (
    catalogueId: number,
    eventId: string,
    sessionCode: string,
  ): Promise<EventRateGroup[]> => {
    const response = await fetcher.get<{ rates: EventRateGroup[] }>(
      `/rce-catalogues/${catalogueId}/events/${eventId}/rates`,
      { params: { session_code: sessionCode } },
    );

    return response.data.rates ?? [];
  },
};
